import axios from '@/plugins/axios';
import { API_BASE_URL } from '@/constants';
import { getStoredToken } from '@/utils/authToken';

// SYN-1774: o backend passou a exigir JWT na rota estática legada
// (/static/clients/client_{id}/{file}) e ganhou o endpoint autenticado
// GET /media/{id}/signed-url?asset=original|heatmap|fogmap, que devolve
// { url, expires_in } — uma GCS signed URL de 5 minutos.
//
// Este composable resolve a URL de exibição/fetch de um asset de mídia de
// forma independente da ordem de deploy:
//   1. caminho principal — pede a signed URL (o axios manda o Bearer) e
//      cacheia por (mediaId, asset) por ~4 min (a URL vale 5);
//   2. fallback — URL estática legada com ?token=<JWT> (mesmo padrão do /ws,
//      pois <img>/<video> não mandam header Authorization): o backend antigo
//      ignora o param extra e o novo o aceita.
// Quando o endpoint de signed URL não existe (backend sem o SYN-1774), o
// veredito "indisponível" é memorizado por 5 min para cada imagem não gerar
// um novo 404.

const SIGNED_URL_TTL_MS = 4 * 60 * 1000;   // margem sobre os 5 min do backend
const UNAVAILABLE_TTL_MS = 5 * 60 * 1000;  // memoriza "endpoint não existe"

const signedUrlCache = new Map();          // `${mediaId}:${asset}` -> { url, expiresAt }
let unavailableUntil = 0;

// O backend informa a validade da URL (`expires_in`, em segundos): 5 min para
// imagem, 30 min para vídeo — o <video> faz Range requests com a MESMA URL a
// cada seek; vencida, o player reassina no `error`. Cacheia com 1 min de
// margem; sem o campo (backend antigo), fica a margem fixa sobre os 5 min.
function cacheTtlMs(expiresIn) {
    if (!Number.isFinite(expiresIn)) return SIGNED_URL_TTL_MS;
    return Math.max(0, expiresIn - 60) * 1000;
}

function getAuthToken() {
    // Mesmo JWT que o WebSocket manda em ?token= (userStore.token): o setUser
    // persiste o access_token em localStorage('token') (sessionStorage sem
    // "Manter conectado", SYN-2738), que é também o que o
    // interceptor do axios lê a cada request. Ler direto do localStorage evita
    // depender de Pinia ativo (estas funções rodam fora de setup()).
    return getStoredToken();
}

/**
 * URL da rota estática legada com o JWT em `?token=` (padrão do /ws).
 * `base` permite trocar a origem — ex.: os exports de PDF buscam via
 * `/proxy-images/api` para manter o fetch same-origin.
 */
export function buildLegacyMediaUrl(clientId, filename, { base = API_BASE_URL } = {}) {
    const url = `${base}/static/clients/client_${clientId}/${filename}`;
    const token = getAuthToken();
    if (!token) return url;
    return `${url}?token=${encodeURIComponent(token)}`;
}

export function useMediaUrl() {
    /**
     * Resolve a URL de um asset de mídia: signed URL quando o backend oferece,
     * senão `legacyUrl` (construa com buildLegacyMediaUrl). `asset` segue o
     * contrato do backend: 'original' | 'heatmap' | 'fogmap'.
     */
    async function resolveMediaUrl({ mediaId, asset = 'original', legacyUrl = null }) {
        if (!mediaId) return legacyUrl;

        const key = `${mediaId}:${asset}`;
        const now = Date.now();

        const cached = signedUrlCache.get(key);
        if (cached && cached.expiresAt > now) return cached.url;
        signedUrlCache.delete(key);

        if (unavailableUntil > now) return legacyUrl;

        try {
            const response = await axios.get(`/media/${mediaId}/signed-url`, {
                params: { asset },
                // 404 aqui é fluxo esperado (backend sem o SYN-1774) — não é bug.
                _skipErrorReport: true,
            });
            const url = response.data?.url;
            if (!url) return legacyUrl;
            signedUrlCache.set(key, { url, expiresAt: now + cacheTtlMs(response.data.expires_in) });
            return url;
        } catch (error) {
            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;
            // 404 "Not Found" = rota inexistente (backend antigo) → memoriza o
            // veredito. O 404 "Media not found" vem da rota nova (media
            // inexistente/cross-tenant) e NÃO significa endpoint indisponível.
            if (status === 501 || (status === 404 && detail !== 'Media not found')) {
                unavailableUntil = now + UNAVAILABLE_TTL_MS;
            }
            return legacyUrl;
        }
    }

    /**
     * Descarta a signed URL cacheada (ex.: fetch falhou porque expirou) para a
     * próxima resolução pedir uma nova. Sem `asset`, limpa todas do mediaId.
     */
    function invalidateMediaUrl(mediaId, asset = null) {
        if (!mediaId) return;
        if (asset) {
            signedUrlCache.delete(`${mediaId}:${asset}`);
            return;
        }
        for (const key of Array.from(signedUrlCache.keys())) {
            if (key.startsWith(`${mediaId}:`)) signedUrlCache.delete(key);
        }
    }

    return { resolveMediaUrl, invalidateMediaUrl, buildLegacyMediaUrl };
}

/**
 * Descarta TODAS as signed URLs (SYN-2602, logout): com 30 min de validade no
 * vídeo, uma URL sobrevivendo ao logout SPA no mesmo dispositivo deixou de ser
 * desprezível. Chamada pelo `clearMediaVideoCache`.
 */
export function clearMediaUrlCache() {
    signedUrlCache.clear();
    unavailableUntil = 0;
}

export function __resetMediaUrlCacheForTests() {
    clearMediaUrlCache();
}
